import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronRight, Clock, ExternalLink, Lock } from "lucide-react";
import { RoseRow } from "@/design-system/RoseRow";
import { MemberRatingPill } from "@/design-system/MemberRatingPill";
import { nairobiDate, NAIROBI_TZ } from "@/lib/datetime";
import { BottomSheet } from "./BottomSheet";

// WW-169 · event page score: three voices (our score, insiders, the internet) and the award.
const API = process.env.REACT_APP_BACKEND_URL;
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD_BG = "var(--ww-surface)";
const KILI = "#FFD60A";
const h2 = { fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 20 };
const body = { fontFamily: "Arial", fontSize: 14, lineHeight: 1.5, color: "var(--ww-text)" };

export const PassedCard = ({ onReview, onPhoto }) => (
  <div data-testid="event-passed-card" className="mt-4 rounded-2xl border p-4" style={{ borderColor: LINE, background: CARD_BG }}>
    <div className="flex items-center gap-3">
      <span style={{ width: 36, height: 36, borderRadius: 999, display: "grid", placeItems: "center", border: `1px solid ${LINE}` }}>
        <Clock size={18} />
      </span>
      <div>
        <p className="lowercase" style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15 }}>this event has passed</p>
        <p style={{ fontFamily: "Arial", fontSize: 13, color: META }}>were you there? say how it went.</p>
      </div>
    </div>
    <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
      <button type="button" data-testid="event-passed-review" onClick={onReview}
        style={{ minHeight: 48, borderRadius: 12, background: KILI, color: "#1A1A30", border: `1px solid ${KILI}`,
                 fontFamily: "Poppins", fontWeight: 800, fontSize: 14, textTransform: "lowercase", cursor: "pointer" }}>
        review this event
      </button>
      <button type="button" data-testid="event-passed-photo" onClick={onPhoto}
        style={{ minHeight: 48, borderRadius: 12, background: "transparent", color: "var(--ww-text)",
                 border: "1px solid var(--ww-line-strong)", fontFamily: "Poppins", fontWeight: 600, fontSize: 14,
                 textTransform: "lowercase", cursor: "pointer" }}>
        add a photo
      </button>
    </div>
    <p className="mt-2" style={{ fontFamily: "Arial", fontSize: 11, color: META, textAlign: "center" }}>
      reviewing also marks that you went
    </p>
  </div>
);

const Row = ({ label, testId, onClick, children, last }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    style={{ width: "100%", minHeight: 52, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
             background: "transparent", border: 0, borderBottom: last ? 0 : `1px solid ${LINE}`, color: "var(--ww-text)",
             cursor: "pointer", textAlign: "left" }}>
    <span className="lowercase" style={{ flex: 1, fontFamily: "Poppins", fontWeight: 600, fontSize: 14 }}>{label}</span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>{children}</span>
    <ChevronRight size={18} style={{ color: META, flexShrink: 0 }} />
  </button>
);

const InsiderPill = ({ average, iko, testId = "insiders-pill" }) => (
  <MemberRatingPill value={average} iko={iko} testId={testId} />
);

const sourcesLabel = (n) => (n === 1 ? "1 source" : `${n} sources`);
const reviewsLabel = (n) => (n === 1 ? "1 review" : `${n} reviews`);

export const HowItWent = ({ score, roses, insiders, sources, onOpen, iko }) => {
  const hasScore = typeof score === "number";
  const n = insiders?.count || 0;
  const rows = [];
  if (hasScore) {
    rows.push(
      <Row key="award" label="watu wako award" testId="how-row-award" onClick={() => onOpen("explain")}>
        {roses ? <RoseRow value={score} colourway="yellow" size={18} showNumber={false} testId="how-award-roses" />
               : <span data-testid="how-award-none" style={{ fontFamily: "Arial", fontSize: 13, color: META }}>no roses</span>}
      </Row>,
      <Row key="score" label="watu wako score" testId="how-row-score" onClick={() => onOpen("explain")}>
        <span data-testid="how-score-value" style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18 }}>{score.toFixed(1)}</span>
      </Row>,
    );
  }
  rows.push(
    <Row key="insiders" label="insiders" testId="how-row-insiders" onClick={() => onOpen("insiders")}>
      {n ? <><InsiderPill average={insiders.average} iko={iko} /><span style={{ fontFamily: "Arial", fontSize: 13, color: META }}>{reviewsLabel(n)}</span></>
         : <span style={{ fontFamily: "Arial", fontSize: 13, color: META }}>no reviews yet</span>}
    </Row>,
  );
  if (sources.length) {
    rows.push(
      <Row key="internet" label="from the internet" testId="how-row-internet" onClick={() => onOpen("internet")}>
        <span style={{ fontFamily: "Arial", fontSize: 13, color: META }}>{sourcesLabel(sources.length)}</span>
      </Row>,
    );
  }
  return (
    <section className="mt-8" data-testid="how-it-went">
      <h2 className="mb-3 lowercase" style={h2}>how it went</h2>
      <div className="overflow-hidden rounded-2xl border" style={{ borderColor: LINE, background: CARD_BG }}>
        {rows.map((r, i) => React.cloneElement(r, { last: i === rows.length - 1 }))}
      </div>
    </section>
  );
};

export const InternetDrawer = ({ title, sources, onClose }) => {
  const lastIso = sources.map((s) => s.last_checked).filter(Boolean).sort().pop();
  const checked = lastIso && new Date(lastIso.length === 10 ? `${lastIso}T12:00:00+03:00` : lastIso)
    .toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone: NAIROBI_TZ }).toLowerCase();
  return (
    <BottomSheet title={title} pill="not our score" onClose={onClose} testId="internet-drawer">
      <ul className="mt-4 grid gap-3" style={{ listStyle: "none", padding: 0 }}>
        {sources.map((s) => (
          <li key={s.id} data-testid={`internet-source-${s.id}`} className="rounded-xl border p-3" style={{ borderColor: LINE, background: CARD_BG }}>
            <div className="flex items-center gap-2">
              <p className="lowercase" style={{ flex: 1, fontFamily: "Poppins", fontWeight: 700, fontSize: 14 }}>{s.name}</p>
              {s.figure && <span style={{ fontFamily: "Arial", fontSize: 13, fontWeight: 700 }}>{s.figure}</span>}
            </div>
            {s.summary && <p className="mt-1" style={{ ...body, fontSize: 13 }}>{s.summary}</p>}
            {s.link && (
              <a href={s.link} target="_blank" rel="noreferrer" data-testid={`internet-source-link-${s.id}`}
                 className="mt-2 inline-flex items-center gap-1 lowercase"
                 style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text)", textDecoration: "underline" }}>
                open source <ExternalLink size={12} />
              </a>
            )}
          </li>
        ))}
      </ul>
      <p data-testid="internet-drawer-fineprint" className="mt-4" style={{ fontFamily: "Arial", fontSize: 11, color: META, lineHeight: 1.5 }}>
        summarised by watu wako in our own words. names not shown.{checked ? ` last checked ${checked}.` : ""} never counts toward the watu wako score.
      </p>
    </BottomSheet>
  );
};

const ExplainSection = ({ title, children, testId }) => (
  <section data-testid={testId} className="mt-5">
    <h3 className="lowercase" style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15 }}>{title}</h3>
    {children}
  </section>
);

const ScoreBar = ({ score }) => {
  const pct = Math.max(0, Math.min(1, (score - 1) / 4)) * 100;
  return (
    <div data-testid="explain-score-visual" className="mt-2 rounded-xl border p-3" style={{ borderColor: LINE, background: CARD_BG }}>
      <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 28 }}>
        {score.toFixed(1)} <span style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 14, color: META }}>out of 5</span>
      </p>
      <div style={{ position: "relative", height: 10, borderRadius: 999, background: "var(--ww-line)", marginTop: 8 }}>
        <div data-testid="explain-score-fill" style={{ width: `${pct}%`, height: "100%", borderRadius: 999, background: KILI }} />
      </div>
      <div className="mt-1 flex justify-between" style={{ fontFamily: "Arial", fontSize: 11, color: META }}>
        {[1, 2, 3, 4, 5].map((n) => <span key={n}>{n}</span>)}
      </div>
    </div>
  );
};

export const ExplainerDrawer = ({ score, insiders, iko, onClose }) => (
  <BottomSheet title="what these mean" onClose={onClose} testId="explainer-drawer">
    <ExplainSection title="award" testId="explain-award">
      <div className="mt-2 grid gap-2 rounded-xl border p-3" style={{ gridTemplateColumns: "1fr 1fr", borderColor: LINE, background: CARD_BG }}>
        <div className="grid justify-items-center gap-1">
          <RoseRow value={4.0} colourway="yellow" size={20} showNumber={false} testId="explain-award-roses" />
          <span style={{ fontFamily: "Arial", fontSize: 12, color: META }}>awarded</span>
        </div>
        <div className="grid justify-items-center gap-1">
          <span style={{ fontFamily: "Arial", fontSize: 13, fontWeight: 700, minHeight: 20 }}>no roses</span>
          <span style={{ fontFamily: "Arial", fontSize: 12, color: META }}>not this time</span>
        </div>
      </div>
      <p className="mt-2" style={body}>roses go to events that earned them, only when the watu wako score is 3.0 or more. no roses means the event did not earn one this time.</p>
    </ExplainSection>
    <ExplainSection title="score" testId="explain-score">
      {typeof score === "number" && <ScoreBar score={score} />}
      <p className="mt-2" style={body}>our team goes to the event and rates it on five things: the experience and vibe, what you came for, the amenities, the flow and getting around. each is averaged, then the five are averaged. what insiders and the internet say never count toward it.</p>
    </ExplainSection>
    <ExplainSection title="insiders" testId="explain-insiders">
      <div data-testid="explain-insiders-visual" className="mt-2 flex items-center gap-3 rounded-xl border p-3" style={{ borderColor: LINE, background: CARD_BG }}>
        {insiders?.count ? (
          <>
            <MemberRatingPill value={insiders.average} iko={iko} large testId="explain-insiders-pill" />
            <span style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
              from {insiders.count} insider{insiders.count === 1 ? "" : "s"}
            </span>
          </>
        ) : (
          <>
            <MemberRatingPill value={null} iko={iko} large testId="explain-insiders-pill" />
            <span style={{ fontFamily: "Arial", fontSize: 13, color: META }}>no reviews yet</span>
          </>
        )}
      </div>
      <p className="mt-1" style={body}>insiders who went rate it too, out of 5, shown apart from our score. tap insiders to read what each one said, with their photos.</p>
    </ExplainSection>
    <ExplainSection title="from the internet" testId="explain-internet">
      <p className="mt-1" style={body}>what other sites and posts say, summed up in our words. shown for context only.</p>
    </ExplainSection>
  </BottomSheet>
);

export const InsidersDrawer = ({ eventId, iko, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/events/${eventId}/reviews`),
      axios.get(`${API}/api/events/${eventId}/posts`).catch(() => ({ data: { posts: [] } })),
    ]).then(([r, p]) => {
      const photos = {};
      (p.data.posts || []).forEach((post) => {
        (photos[post.author_user_id] ||= []).push(...(post.media_urls || []));
      });
      setRows((r.data.reviews || []).map((rv) => ({ ...rv, photos: photos[rv.author_user_id] || [] })));
    }).catch(() => setRows([]));
  }, [eventId]);
  return (
    <BottomSheet title="insiders" onClose={onClose} testId="insiders-drawer">
      {rows === null && <p className="mt-4" style={{ ...body, color: META }}>loading...</p>}
      {rows?.length === 0 && <p data-testid="insiders-empty" className="mt-4" style={{ ...body, color: META }}>no reviews yet</p>}
      <ul className="mt-4 grid gap-3" style={{ listStyle: "none", padding: 0 }}>
        {(rows || []).map((rv) => {
          const d = nairobiDate(rv.created_at);
          return (
            <li key={rv.id} data-testid={`insider-review-${rv.id}`} className="rounded-xl border p-3" style={{ borderColor: LINE, background: CARD_BG }}>
              <div className="flex items-center gap-2">
                <InsiderPill average={rv.member_score} iko={iko} testId={`insider-review-rating-${rv.id}`} />
                <p className="lowercase" style={{ flex: 1, fontFamily: "Poppins", fontWeight: 700, fontSize: 14 }}>
                  {(rv.author?.display_name || "insider").toLowerCase()}
                  {rv.author?.handle && <span style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 12, color: META }}> @{rv.author.handle}</span>}
                </p>
                {d && <span style={{ fontFamily: "Arial", fontSize: 11, color: META }}>
                  {d.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, day: "numeric", month: "short" }).toLowerCase()}
                </span>}
              </div>
              {rv.note && <p className="mt-2" style={{ ...body, fontSize: 13, whiteSpace: "pre-wrap" }}>{rv.note}</p>}
              {rv.photos.length > 0 && (
                <div className="mt-2 flex gap-2 overflow-x-auto">
                  {rv.photos.map((u) => <img key={u} src={`${API}${u}`} alt="" style={{ width: 88, height: 88, objectFit: "cover", borderRadius: 8 }} />)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </BottomSheet>
  );
};

export const UpcomingReviewsLock = ({ startAt }) => {
  const d = nairobiDate(startAt);
  const day = d ? d.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, weekday: "short", day: "numeric", month: "long" }).toLowerCase().replace(",", "") : "";
  const time = d ? d.toLocaleString("en-GB", { timeZone: NAIROBI_TZ, hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase().replace(" ", "").replace(/^0:/, "12:") : "";
  return (
    <section className="mt-8" data-testid="reviews-lock">
      <h2 className="mb-3 lowercase" style={h2}>reviews</h2>
      <div className="flex items-center gap-3 rounded-2xl border p-4" style={{ borderColor: LINE, background: CARD_BG }}>
        <Lock size={18} style={{ color: META, flexShrink: 0 }} />
        <p data-testid="reviews-lock-text" style={{ ...body, color: META }}>
          {d ? `reviews open when the event starts, on ${day} at ${time}.` : "reviews open when the event starts."}
        </p>
      </div>
    </section>
  );
};

// WW-146.j · safari items: no score, a plain reviews note.
export const SafariReviewsBox = () => (
  <section className="mt-8" data-testid="safari-reviews-box">
    <h2 className="mb-3 lowercase" style={h2}>reviews</h2>
    <div className="rounded-2xl border p-4" style={{ borderColor: LINE, background: CARD_BG }}>
      <p data-testid="safari-reviews-text" style={{ ...body, color: META }}>
        safari items are listed, not scored.
      </p>
    </div>
  </section>
);

export const CuratorInternetRow = ({ count, onOpen }) => (
  <div className="mt-4 overflow-hidden rounded-2xl border" style={{ borderColor: LINE, background: CARD_BG }}>
    <Row label="from the internet · about this curator" testId="curator-internet-row" onClick={onOpen} last>
      <span style={{ fontFamily: "Arial", fontSize: 13, color: META }}>{sourcesLabel(count)}</span>
    </Row>
  </div>
);
